<script lang="ts">
import AppAccordionSingle from '@/components/common/AppAccordionSingle.vue'
import AppIcon from '@/components/icons/AppIcon.vue'
import AppModal from '@/components/modals/AppModal.vue'
import { useTailwind } from '@/plugins/useTailwind'
import type { Image, InstantWinPrize, Nullable, Prize } from '@/types'
import { formatDateTime, isInPast } from '@/utils/date-time-utils'
import { isInstantWinPrize, isPrize } from '@/utils/type-guard-utils'
import { type PropType, defineComponent } from 'vue'

export default defineComponent({
  name: 'HighlightsModal',
  components: {
    AppModal,
    AppAccordionSingle,
  },
  props: {
    prize: {
      type: Object as PropType<InstantWinPrize | Prize>,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    open: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isMd } = useTailwind()

    return {
      isInPast,
      isPrize,
      formatDateTime,
      isMd,
    }
  },
  computed: {
    prizeImage(): Nullable<Image> {
      return isInstantWinPrize(this.prize)
        ? (this.prize?.modal_image ?? this.prize.image)
        : this.prize.card_image
    },
  },
  methods: {
    handleDescriptionToggle(_open: boolean) {
      if (this.isMd) {
        return
      }

      const descriptionEl = this.$refs
        .descriptionAccordion as HTMLElement | null

      if (!descriptionEl) {
        return
      }

      void this.$nextTick(() => {
        descriptionEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
  },
})
</script>

<template>
  <AppModal
    :id="id"
    :open="open"
    close-button-class="right-6 top-2 z-10 bg-gray-200"
  >
    <template #button="{ openModal }">
      <slot name="button" :open-modal="openModal" />
    </template>

    <template #default="{ closeModal }">
      <div class="flex flex-col overflow-x-hidden lg:w-[30dvw]">
        <div class="flex-1 overflow-y-auto">
          <!-- Header -->
          <div class="relative h-fit pb-1">
            <img
              v-if="prizeImage"
              :src="prizeImage.src"
              :alt="prizeImage.alt"
              class="h-[300px] w-full object-cover object-center md:h-[400px]"
            />
            <div class="flex h-fit gap-2.5 p-3 pb-0">
              <h2 class="text-xl font-bold text-gray-900">
                {{ prize.title }}
              </h2>
            </div>
          </div>

          <!-- Draw details -->
          <div v-if="isPrize(prize)" class="gap-2 p-3 pt-2">
            <div
              class="flex flex-col rounded-lg border border-gray-200 p-2 shadow-sm"
            >
              <div class="flex gap-1">
                <p>
                  Date
                  <template v-if="isInPast(prize.close_at)">closed</template>
                  <template v-if="!isInPast(prize.close_at)">closes</template>:
                </p>
                <p class="flex-grow text-right font-bold">
                  {{ formatDateTime(prize.close_at) }}
                </p>
              </div>

              <div class="flex gap-1">
                <p>Date drawn:</p>
                <p class="flex-grow text-right font-bold">
                  {{ formatDateTime(prize.drawn_at) }}
                </p>
              </div>
            </div>
          </div>

          <!-- Highlights -->
          <div v-if="prize.highlights?.length" class="gap-2 p-3 pt-2">
            <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
              <div
                v-for="spec in prize.highlights"
                :key="spec.title"
                class="flex space-x-3 rounded-lg border border-gray-200 p-2 shadow-sm"
              >
                <div class="flex items-start space-x-3">
                  <AppIcon :icon="spec.icon" size="md" class="h-auto" />
                  <div>
                    <div class="text-xs font-medium">
                      {{ spec.title }}
                    </div>
                    <div class="text-base font-bold">
                      {{ spec.value ?? spec.text }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Description Accordion -->
          <div
            v-if="prize.description"
            ref="descriptionAccordion"
            class="overflow-hidden"
          >
            <AppAccordionSingle
              name="description-accordion"
              summary-class="border-t border-gray-300 px-4 py-3"
              details-class="border-b border-gray-300 px-4 py-3"
              :open="!prize.highlights?.length"
              @update:open="handleDescriptionToggle"
            >
              <template #title>
                <span class="gap-2 text-base font-bold text-gray-900">
                  Description
                </span>
              </template>
              <template #text>
                <p class="description" v-html="prize.description" />
              </template>
            </AppAccordionSingle>
          </div>
        </div>

        <!-- Footer - Always visible at bottom -->
        <div class="flex-shrink-0 border-t border-gray-200 p-5">
          <div class="text-center">
            <a
              class="cursor-pointer underline hover:no-underline"
              @click="closeModal"
            >
              Dismiss
            </a>
          </div>
        </div>
      </div>
    </template>
  </AppModal>
</template>

<style lang="scss" scoped>
.description {
  @apply break-words text-sm leading-relaxed;
}
</style>
